/* eslint-disable react/no-danger */
import * as React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import H4 from '@base/H4/H4';
import { useSelector } from 'react-redux';
import Paper from '@base/Paper/Paper';
import H5 from '@base/H5/H5';
import { breakpoints } from '@styles/index';
import { useRouter } from 'next/router';
import { PRACTICE_FINISHED, PRACTICE_STARTED } from '@constants/segment';
import Question from './PracticeQuestion';
import TestResults from './TestResults';
import usePracticeSegmentAnalytics from '../usePracticeSegmentAnalytics';

const Container = styled.div`
  margin-bottom: 40px;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-bottom: 56px;
  }
`;

const Questions = styled(Paper)`
  padding: 32px;
  margin-top: 32px;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-top: 24px;
    padding: 16px;
  }
`;

const CurrentQuestion = styled(H5)`
  font-weight: 400;
  color: var(--color-text-primary);
  margin-bottom: 16px;
`;

function FreeQuiz({ callback }) {
  const router = useRouter();
  const { topicUID, conceptUID, goalUID } = router.query;
  const [isTestCompleted, setIsTestCompleted] = React.useState(false);
  const [score, setScore] = React.useState(0);
  const [attemptedQuestions, setAttemptedQuestions] = React.useState([]);
  const [currentQuestion, setCurrentQuestion] = React.useState(1);
  const { questions_data, concept_name } = useSelector(
    ({ freePracticeSession }) => freePracticeSession.questions
  );
  const segmentAnalytics = usePracticeSegmentAnalytics(goalUID);
  React.useEffect(() => {
    if (isTestCompleted || currentQuestion) {
      document.documentElement.scrollTo(0, 0);
    }
  }, [isTestCompleted, currentQuestion]);
  const practiceAgain = () => {
    setScore(0);
    setCurrentQuestion(1);
    setIsTestCompleted(false);
    setAttemptedQuestions([]);
  };
  React.useEffect(() => {
    practiceAgain();
    if (questions_data.length) {
      segmentAnalytics(PRACTICE_STARTED, {
        topic_uid: conceptUID || topicUID,
        practice_type: 'Practice',
        practice_questions: questions_data.length,
        topology_level: 'Topic',
        topic_name: concept_name,
        is_subscription_active: false
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath]);
  if (!questions_data.length) return null;
  const isLast = currentQuestion === questions_data.length;
  const previousQuestion = () => {
    setCurrentQuestion((current) => current - 1);
  };
  const updateScore = ({ isCorrect, selected }) => {
    if (!attemptedQuestions[currentQuestion - 1]) {
      setAttemptedQuestions((c) => [
        ...c,
        { ...questions_data[currentQuestion], selected }
      ]);
      if (isCorrect) {
        setScore((s) => s + 1);
      }
    }
  };
  const nextQuestion = () => {
    if (currentQuestion < questions_data.length) {
      setCurrentQuestion((current) => current + 1);
    }
  };
  const finishTest = () => {
    setIsTestCompleted(true);
    segmentAnalytics(PRACTICE_FINISHED, {
      topic_uid: conceptUID || topicUID,
      num_correct_answers: score,
      topic_name: concept_name,
      is_subscription_active: false,
      practice_type: 'Practice'
    });
    callback();
  };
  return questions_data.length > 0 ? (
    <Container>
      <H4 color="var(--color-text-primary)">Quick practice</H4>
      {isTestCompleted ? (
        <TestResults
          result={`${score}/${questions_data.length}`}
          practiceAgain={practiceAgain}
        />
      ) : (
        <Questions>
          <CurrentQuestion color="var(--color-text-primary)">
            {`Question ${currentQuestion} of ${questions_data.length}`}
          </CurrentQuestion>
          <Question
            question={questions_data[currentQuestion - 1]}
            nextQuestion={nextQuestion}
            previousQuestion={previousQuestion}
            finishTest={finishTest}
            isLast={isLast}
            currentQuestion={currentQuestion}
            attemptedQuestion={attemptedQuestions[currentQuestion - 1] || {}}
            updateScore={updateScore}
          />
        </Questions>
      )}
    </Container>
  ) : null;
}

FreeQuiz.propTypes = {
  callback: PropTypes.func
};

FreeQuiz.defaultProps = {
  callback: () => {}
};

export default FreeQuiz;
